<template>
    <div class="section-wrapper">
        <!-- Amount Warning Info -->
        <infobox-generic :label="t('transfers.instantBAI.step1.infoContent')"/>
        <!-- Anual Available Amount -->
        <text-blocks-generic
        class="info-block"
        :params="availableAnualAmount"/>
        <!-- Monthly Available Amount  -->
        <text-blocks-generic
        class="info-block"
        :params="availableMonthlyAmount"/>
        <!-- Currency -->
        <text-blocks-generic :params="currenciesParams"/>
        <!-- Amount Inputs  -->
        <input-amount
        id="amount"
        class="inputs-size"
        :params="amountParams"
        :inputValue="formData.amount"
        @input="amountInputUpdateHandler"
        @onBlur="amountValidation"/>
        <!-- Calculate Estimated Amount-->
        <button-tertiary
        :class="{'ajust-button-size': getMobileAppState}"
        :label="t('transfers.instantBAI.step1.calculateEstimatedAmount')"
        :disabled="!formData.internationalTransferCurrencyExternalId || !formData.amount"
        @clicked="emit('calculateEstimatedAmount')"/>
        <!-- Estimated Amount Warning Info -->
        <infobox-generic
        v-if="formData.estimatedAmount"
        :label="t('transfers.instantBAI.step1.estimatedAmountNoteInstant', { link: 'https://www.bancobai.ao/pt/precario', target: '_blank'})"/>
        <!-- Estimated Amount -->
        <text-blocks-generic
        class="info-block"
        v-if="formData.estimatedAmount"
        :params="estimatedAmountParams"/>
    </div>
</template>

<script setup>

import InfoboxGeneric from '@/components/generic/infobox/Generic'
import TextBlocksGeneric from '@/components/generic/textBlocks/Generic'
import DropdownCurrency from '@/components/generic/dropdowns/currency/Currency'
import InputAmount from '@/components/generic/inputs/Amount'
import ButtonTertiary from '@/components/generic/buttons/Tertiary'

/* Vue && Assets */
import { ref, computed, watch } from 'vue'
import { amountFormat } from '@/assets/scripts/amounts'
import { getEmptyFieldsIdentifiers } from '@/assets/scripts/objects'
import { getElementOffsetTopPosition } from '@/assets/scripts/htmlManipulators'

/* Store */
import { mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

const { getCurrenciesList, getCustomerLimits, getCustomerPlafonds } = mapGetters(moduleMapper.TRANSFERS_INSTANT_BAI)
const { getMobileAppState } = mapGetters()

/** Props
@formData {Object} - Object with form data
@showInputsErrors {Boolean} - Prop to trigger display of inputs errors
@clearForm {Boolean} - Boolean controller to clear form and errors
@estimatedAmountError {Boolean} Boolean controller to apply specific amount error
@fieldsWithError {Array} - Input List with error MDW provides to use
*/
const props = defineProps({
    formData: Object,
    showInputsErrors: Boolean,
    clearForm: Boolean,
    estimatedAmountError: Boolean,
    fieldsWithError: Array
})

const emit = defineEmits([
    'updateFormData',
    'calculateEstimatedAmount',
    'resetShowInputsErrors',
    'formIsCleared',
    'scrollTo'
])

const availableAnualAmount = computed(() => {
    return {
        label: t('transfers.international.genericLabels.availableAnualAmount'),
        content: amountFormat(getCustomerLimits.value.remainingAmount, getCustomerLimits.value.currency)
    }
})

const availableMonthlyAmount = computed(() => {
    return {
        label: t('transfers.international.genericLabels.availableMonthlyAmount'),
        content: amountFormat(getCustomerPlafonds.value.plafond, getCustomerLimits.value.currency)
    }
})

const currenciesParams = ref({
    label: t('transfers.instantBAI.genericLabels.currency'),
    content: props.formData.internationalTransferCurrencyExternalId
})

const amountParams = ref({
	labelText: t('transfers.instantBAI.genericLabels.amountToTransfer'),
	mandatory: true,
	errorMessage: '',
	errorState: false
})

const amountValidation = () => {
    if (props.formData.amount.length && props.formData.amount <= 0) {
        amountParams.value.errorState = true
        amountParams.value.errorMessage = t('invalidAmount')
    }
}

const estimatedAmountParams = computed(() => {
    return {
        label: t('transfers.instantBAI.genericLabels.estimatedAmount'),
        content: props.formData.estimatedAmount ? amountFormat(props.formData.estimatedAmount, props.formData.accountCurrency) : ''
    }
})

watch(() => props.estimatedAmountError, newVal => {
    if (newVal) {
        amountParams.value.errorState = true
        amountParams.value.errorMessage = t('transfers.instantBAI.step1.amountUnavailable')
    }
})

const amountInputUpdateHandler = event => {
    emit('updateFormData', { field: 'amount', inputValue: event })
    clearFieldError('amount')
}

/**********************************************************************
*                                                                     *
*                          Mandatory Fields                           *
*                                                                     *
***********************************************************************/

const amountsMandatoryList = ref([
    'internationalTransferCurrencyId',
    'amount'
])

const fieldsWithErrorMessage = ref({
    internationalTransferCurrencyId: currenciesParams.value,
    amount: amountParams.value
})

const showInputsErrorsHandler = () => {
	/* Logic to scroll to first emptyField */
	const mandatoryFieldsEmptyList = getEmptyFieldsIdentifiers(props.formData, amountsMandatoryList.value)

    if (mandatoryFieldsEmptyList.length) {
        mandatoryFieldsEmptyList.forEach(fieldName => {
            fieldsWithErrorMessage.value[fieldName].errorMessage = t('mandatoryFieldError')
            fieldsWithErrorMessage.value[fieldName].errorState = true
        })

        emit('scrollTo', getElementOffsetTopPosition(mandatoryFieldsEmptyList[0]), 'showInputsErrorsHandler')
    }

    if (props.fieldsWithError.length) {
        props.fieldsWithError.forEach(field => {
            if (fieldsWithErrorMessage.value[field.parsedFieldOnError]) {
                fieldsWithErrorMessage.value[field.parsedFieldOnError].errorState = true
                fieldsWithErrorMessage.value[field.parsedFieldOnError].errorMessage = field.errorMessage
            }
        })

        emit('scrollTo', getElementOffsetTopPosition(props.fieldsWithError[0]))
    }
}

watch(() => props.showInputsErrors, newVal => {
	if (newVal) showInputsErrorsHandler()
	emit('resetShowInputsErrors')
})

/**********************************************************************
*                                                                     *
*                           Clear Field Errors                        *
*                                                                     *
***********************************************************************/

const clearFieldError = (fieldName) => {
    fieldsWithErrorMessage.value[fieldName].errorMessage = null
    fieldsWithErrorMessage.value[fieldName].errorState = false
}

const clearFormErrors = () => {
	for (var fieldName in fieldsWithErrorMessage.value) {
		clearFieldError(fieldName)
	}
}

watch(() => props.clearForm, () => {
	clearFormErrors()
	emit('formIsCleared')
})
</script>

<style lang="scss" scoped>
.section-wrapper {
    width: 100%;
    gap: 24px;

    display: flex;
    justify-content: flex-start;
    flex-direction: column;

    .ajust-button-size {
        align-self: center;
        width: 100%;
    }
}
</style>
